import {pluralize, React} from '@api'
import {TabNode} from '@flexlayout'
import {CatalogItemCardShape, createWhiteboardTab, createDefaultStyle, WhiteboardPage, WhiteboardStore} from '@modules/whiteboard'
import {CatalogStore, site, StackView} from '@stores'
import {CatalogInstanceChildStore} from '@stores/catalog/CatalogInstanceChildStore'
import {synchronizeStores} from '@stores/SynchronizeStores'
import Vec from '@tldraw/vec'
import {action, observable, runInAction, computed, toJS, autorun, when, reaction} from 'mobx'
import {IDisposer} from 'mobx-utils'
import {_} from '../../shared/components/3rd-party'

export class CatalogSelectionStore extends CatalogInstanceChildStore {
  constructor(public catalog: CatalogStore) {
    super(catalog)

    synchronizeStores(() => {
      when(() => catalog.settingsStore.urlProcessed, () => {
        this.selectedIds.replace(new Set<string>(this.catalog.settings.selectedCatalogIds))

        this._toDispose.push(
          autorun(() => {
            const {selectedIds: {size}, selectedIds} = this
            runInAction(() => {
              const {settings} = catalog
              if (settings.selectedCatalogIds?.length != selectedIds?.size || !settings.selectedCatalogIds?.every(id => selectedIds.has(id))) {
                settings.selectedCatalogIds = Array.from(selectedIds.keys())
              }
            })
          }, {name: `Update catalog serialized settings for selection`}),
          autorun(() => {
            const {focusedCatalogItemId} = catalog
            runInAction(() => {
              const {hoverPopoverItemId} = catalog

              // console.log({hoverPopoverItemId, focusedCatalogItemId})
              if (hoverPopoverItemId && focusedCatalogItemId && hoverPopoverItemId != focusedCatalogItemId) {
                catalog.hoverPopoverItemId = null
              }
            })
          }, { name: `When selection changes, close any hover popover`}),
        )
      })
    })
  }

  _toDispose: Array<IDisposer> = []

  @action dispose = () => {
    this._toDispose.forEach(d => d())
    // console.log(`dispose ${this.stores._id} catalog selection`)
  }

  get freezeIds() {
    return this.catalog.settings.freezeIds
  }

  @computed get selectedItemsLabel() {
    const {selectedItemCount, freezeIds} = this
    return `${selectedItemCount || freezeIds.length} item${selectedItemCount != 1 ? 's' : ''} Selected`
  }

  @computed get selectedItemCount() {
    const {selectedIds, catalog: {selectAll, itemCount, groupBy: {isProductView}}} = this
    if (isProductView) {
      return selectAll ? itemCount : selectedIds.size
    }
    // else if (lastGroupInHierarchy) {
    //   const {groupsWithSelectAll} = lastGroupInHierarchy
    //   if (!_.isEmpty(groupsWithSelectAll)) {
    //     return _.sum(groupsWithSelectAll.map(g => g.store.totalCount)) + selectedIds.size - Array.from(selectedIds.keys()).filter(id => groupsWithSelectAll.some(g => _.has(g.productStore?.itemPositionByID, id))).length
    //   }
    // }

    return selectedIds.size
  }

  selectedIds = observable.set<string>()

  @observable duplicateItemId: string = null

  @observable publishCompleted: boolean = true // this state is used to restrict update of item count on library selector during publishing

  @action('Clear CatalogItems Selection') clear = () => {
    this.selectedIds.clear()
  }

  @action clearInvalidCatalogItemSelections = _.debounce(() => {
    if (!this.groupBy?.catalogItemViewStore?.catalogItemIdsArray) return

    const {selectedIds, catalog: {isProductView}, groupBy: {lastGroupInHierarchy, catalogItemViewStore: {catalogItemsDict}}} = this

    if (selectedIds.size === 0) {
      return
    } else {
      Array.from(selectedIds.keys()).forEach(selectedId => {
        if (isProductView) {
          if (!_.has(catalogItemsDict, selectedId)) {
            selectedIds.delete(selectedId)
          }
        } else {
          const split = selectedId.split('.'),
            id = _.last(split),
            groupId = split.slice(0, split.length - 1).join('.'),
            dict = lastGroupInHierarchy.nodeFor(groupId)?.productStore?.catalogItemsDict

          if (!_.has(dict, id)) {
            selectedIds.delete(id)
          }
        }
      })
    }
  }, 1000) // Wait for other groups to load

  @action askDeleteSelectedItems = () => {
    const {selectedIds, catalog: {dialogs: {showConfirmationProductPopup}, catalogItemName}} = this
    selectedIds.size > 0 && showConfirmationProductPopup({
      message: `Are you sure you want to delete ${this.selectedIds.size == 1 ? `this ${catalogItemName}` :
                                                  `these ${this.selectedIds.size.toLocaleString()} ${catalogItemName}s`}?`,
      operation: 'DELETE_PRODUCTS'
    })
  }

  @computed get filteredCatalogIds() {
    return new Set<string>(this.catalog.settings.freezeIds)
  }

  @action freezeSelectedIds = () => {
    this.catalog.settings.freezeIds = [...Array.from(this.selectedIds.keys())]
    this.selectedIds.clear()
  }

  @action resetFreezeIds = () => {
    this.catalog.settings.freezeIds = []
  }

  @action updatePublishCompleted = (val) => this.publishCompleted = val

  @computed get showSelectionButton() {
    return this.selectedIds.size > 0
  }

  @computed get showFreezeMenuButton() {
    return (!this.catalog.isShowingAssortment && this.selectedIds.size > 1 && this.selectedIds.size !== this.catalog.itemCount) || (this.selectedIds.size === 1 && this.catalog.itemCount > 1)
  }

  @action deleteSelectedProducts = () => this.catalog.dialogs.showConfirmationProductPopup({
    message: `Are you sure you want to delete ${this.selectedIds.size == 1 ? 'this product' :
                                                `these ${this.selectedIds.size.toLocaleString()} products`}?`,
    operation: 'DELETE_PRODUCTS'
  })

  @action duplicateSelectedCatalogItems = () => this.catalog.dialogs.showConfirmationProductPopup({
    message: `Are you sure you want to duplicate ${this.selectedIds.size == 1 ? 'this product' :
                                                   `these ${this.selectedIds.size.toLocaleString()} products`}?`,
    operation: 'DUPLICATE_PRODUCTS'
  })

  @computed get selectedCatalogItems() {
    const {selectedIds, catalog} = this,
      {catalogItemById, parseIdFromPath, componentStores: {cards}} = catalog
    return Array.from(selectedIds.values()).map(id => cards[id]).filter(_.identity)
  }

  @computed get allowDuplicate() {
    const {selectedCatalogItems, catalog} = this,
      {isActionButtonEnabled} = catalog

    return selectedCatalogItems.every(val => isActionButtonEnabled('duplicate', val))
  }

  @computed get allowDelete() {
    const {selectedCatalogItems, catalog} = this,
      {isActionButtonEnabled} = catalog

    return selectedCatalogItems.every(val => isActionButtonEnabled('delete', val))
  }

  @action askDuplicateSingleCatalogItem = () => {
    this.catalog.dialogs.showConfirmationProductPopup({
      message: `Are you sure you want to duplicate this ${this.catalog.catalogItemName}?`,
      operation: 'DUPLICATE_SINGLE_PRODUCT'
    })
  }

  @action askDuplicateSelectedCatalogItems = () => {
    this.selectedIds.size > 0 && this.catalog.dialogs.showConfirmationProductPopup({
      message: `Are you sure you want to duplicate ${this.selectedIds.size == 1 ? `this ${this.catalog.catalogItemName}` :
                                                     `these ${this.selectedIds.size.toLocaleString()} ${this.catalog.catalogItemName}s`}?`,
      operation: 'DUPLICATE_PRODUCTS'
    })
  }

  @action deleteSelectedItems = async () => {
    const {selectedIds, selectedIds: {size}, catalog: {parseIdFromPath, mutations: {deleteCatalogItemsById}}} = this

    if (size > 0) {
      const ids = Array.from(selectedIds.keys()).map(parseIdFromPath)
      selectedIds.clear()

      await deleteCatalogItemsById(ids)
    }
  }

  @action selectRange = (fromId: string, toId: string, idsWithPath: Array<string>) => {
    const {selectedIds} = this
    let fromIndex = idsWithPath.findIndex(v => v == fromId)
    let toIndex = idsWithPath.findIndex(v => v == toId)

    //let extraIds = []
    if (fromIndex == -1 || toIndex == -1) {
      fromIndex = fromIndex == -1 ? 0 : fromIndex
      toIndex = toIndex == -1 ? idsWithPath.length - 1 : toIndex
      //extraIds = Array.from(selected.keys())
    }

    let final = {from: Math.min(fromIndex, toIndex), to: Math.max(fromIndex, toIndex)}

    selectedIds.clear()
    for (let i = final.from; i <= final.to; i++) {
      selectedIds.add(idsWithPath[i])
    }
    //extraIds.forEach(id => selected.add(id))
  }

  @computed get offlineSelectedIds() {
    return Array.from(this.selectedIds).map(id => this.catalog.parseIdFromPath(id)).filter(id => _.isString(id) && this.catalog.isOfflineItem(id))
  }

  @computed get offlineValidSelectedIds() {
    const {catalog: {offlineItemsByIndex, index, validateCatalogItem}} = this
    return this.offlineSelectedIds.filter((id) => offlineItemsByIndex?.[index]?.[id] && _.isEmpty(validateCatalogItem(offlineItemsByIndex[index][id])))
  }

  @action onClickItem = (e: React.MouseEvent | Partial<MouseEvent>, id, items: Array<string> = null, stackView: StackView = null) => {
    //console.trace(`onClickItem`, {id})
    let {selectedIds, catalog, catalog: {focusedCatalogItemId, catalogConfig}} = this

    if (selectedIds.size == 1) {
      focusedCatalogItemId = _.first(Array.from(selectedIds.keys()))
    }

    if (e.shiftKey && focusedCatalogItemId && items) {
      //this.focused = id
      this.selectRange(focusedCatalogItemId, id, items)

      //console.log(`Toggle selection for objects from ${focused} to ${id}  - ${fromIndex} to ${toIndex}`, objects)
      e.preventDefault()
      e.stopPropagation()
    } else if (e.ctrlKey || e.metaKey || (catalogConfig?.features?.stackedCards && stackView == 'collapsed' || stackView == 'spread')) {
      // Add or remove the item clicked

      if (catalog.selectAll) {
        selectedIds.clear()
        _.keys(catalog.catalogItemById).forEach(id => selectedIds.add(id))
        catalog.filteredOfflineItems.forEach(item => selectedIds.add(item._id))
      }

      if (selectedIds.has(id)) {
        selectedIds.delete(id)
      } else {
        if (catalogConfig?.features?.stackedCards && (catalogConfig?.features?.stackedCards && (stackView == 'collapsed' || stackView == 'spread'))) {
          selectedIds.clear()
        }

        selectedIds.add(id)
      }
      catalog.focusedCatalogItemId = selectedIds.size > 0 ? id : null
    } else {
      if (selectedIds.size == 1 && selectedIds.has(id)) {
        //this.onSelectProduct(id)
        //selectedIds.clear()
      } else {
        if (site.touchDevice) { // toggle on click - todo - enable for touch
          selectedIds.has(id) ? selectedIds.delete(id) : selectedIds.add(id)
        } else {
          if (selectedIds.size != 1 || !selectedIds.has(id)) {
            selectedIds.clear()
            selectedIds.add(id)
          } else if (selectedIds.size == 1 && selectedIds.has(id)) {
            selectedIds.delete(id)
          }
        }
      }
      this.catalog.focusedCatalogItemId = selectedIds.size > 0 ? id : null
    }

    catalog.selectAll = false
  }

  @action toggleSelectedInlineDetails = () => {
    const {catalog: {settings: {perItem}}, selectedIds} = this,
      ids = Array.from(selectedIds.keys()),
      allToggled = ids.every(id => perItem[id]?.lod)

    ids.forEach(id => _.merge(perItem, {[id]: {lod: allToggled ? 0 : 1}}))
  }

  @action uploadSelectedToC8 = async () => {
    const {catalog: {offlineItemsByIndex, index, createItem, validateCatalogItem}, offlineSelectedIds: ids} = this
    this.updatePublishCompleted(false)
    ids.forEach((id, i) => {
      const offline = offlineItemsByIndex[index][id]
      if (offline && _.isEmpty(validateCatalogItem(offline))) {
        createItem.createObjectInRemote(toJS(offline)).then(() => {
          if (i == ids.length - 1) {
            this.updatePublishCompleted(true)
          }
        })
      }
    })
  }

  /// region Whiteboard

  @action sendSelectionToWhiteboard = (props: { tab?: TabNode, page?: WhiteboardPage, idToSend?: string } = {}) => {
    let tab = props.tab
    if (!tab) {
      // Do we have a tab we can reuse?
      const {flexLayout: {tabsByComponent}} = this,
        whiteboards = tabsByComponent['whiteboard'] ?? []

      // Try and use an active whiteboard tab, otherwise find one that's not selected, otherwise create one
      tab = whiteboards.find(w => w.isSelected) ?? _.first(whiteboards)

      if (!tab) {
        tab = createWhiteboardTab()
        tab.move('right')
      }
    }

    tab.selectInTabSet()

    console.log(tab, props)
    when(
      () => tab.extraData?.whiteboard != null,
      () => {
        const whiteboard = tab.extraData.whiteboard as WhiteboardStore

        runInAction(() => {
          // Create a page if needed
          let page = props.page

          if (!page) {
            page = whiteboard.page ?? whiteboard.createNewPage()
          }

          whiteboard.selectPage(page)

          // Create objects that don't already exist

          const {catalog: {dialogs: {showToastMessage}, componentStores}} = this,
            catalogItemIds = props.idToSend ? [props.idToSend] : Array.from(this.selectedIds.keys()),
            cardStores = catalogItemIds.map(id => componentStores.cards[id])

          console.log({cardStores, catalogItemIds})
          const results = whiteboard.createUtil.createBuilderCards(cardStores)

          site.showToastMessage(
            _.isEmpty(results) ? `Unable to send items to whiteboard.` :
                                 `Sent ${results.length} ${pluralize('Item', results.length)} to Whiteboard`)
        })
      })
  }
  /// endregion

  get settings() { return this.catalog.settings}

  @action selected_toggleActive = async () => {
    const {selectedCatalogItems, flexLayout: {refetchAllCatalogRelatedTabs}, settings, settings: {autoSaveInlineChanges}} = this
    settings.autoSaveInlineChanges = false
    await Promise.all(selectedCatalogItems.map(async v => await v.onToggleActive(null, true)))
    settings.autoSaveInlineChanges = autoSaveInlineChanges
    refetchAllCatalogRelatedTabs()
  }

  @action selected_setAllActive = async () => {
    const {selectedCatalogItems, flexLayout: {refetchAllCatalogRelatedTabs}, settings, settings: {autoSaveInlineChanges}} = this
    settings.autoSaveInlineChanges = false
    await Promise.all(selectedCatalogItems.map(async v => await v.onSetActiveInActive(true, true)))
    settings.autoSaveInlineChanges = autoSaveInlineChanges
    refetchAllCatalogRelatedTabs()
  }

  @action selected_setAllInactive = async () => {
    const {selectedCatalogItems, flexLayout: {refetchAllCatalogRelatedTabs}, settings, settings: {autoSaveInlineChanges}} = this
    settings.autoSaveInlineChanges = false
    await Promise.all(selectedCatalogItems.map(async v => await v.onSetActiveInActive(false, true)))
    settings.autoSaveInlineChanges = autoSaveInlineChanges
    refetchAllCatalogRelatedTabs()
  }

  @action selected_refreshImages = async () => {
    const {selectedCatalogItems} = this
    await Promise.all(selectedCatalogItems.map(async selectedItem => {
      await selectedItem.onRefreshImages()
    }))

  }
}



// WEBPACK FOOTER //
// ./src/stores/catalog/CatalogSelectionStore.ts